// ============================================
// moldClaw - Payment Hook (Polar Checkout)
// ============================================

/**
 * 🧑‍💻 SWE-1: 결제 훅 (Polar 연동)
 * 🧑‍💻 SWE-2: 상태 관리
 * 🎨 CX-1: UX 피드백
 * 
 * Polar Checkout 플로우:
 * 1. 프론트에서 백엔드 API 호출 (POST /checkout)
 * 2. 백엔드가 Polar checkout URL 반환
 * 3. 프론트에서 해당 URL로 redirect
 * 4. 결제 완료 후 success_url로 redirect
 * 5. Webhook으로 서버에서 구독 활성화
 */

import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useAuth';
import {
  createPolarCheckout,
  getPolarSubscription,
  getPolarCheckoutStatus,
  cancelPolarSubscription,
  createPolarPortalSession,
  PolarSubscription,
  PolarCheckoutStatus,
  ApiException,
  safeRedirect,
} from '../lib/api';

// ============================================
// Types
// ============================================

interface UsePaymentReturn {
  // 상태
  isLoading: boolean;
  error: string | null;
  subscription: PolarSubscription | null;
  
  // 액션
  loadSubscription: () => Promise<void>;
  checkout: (tier: 'starter' | 'pro' | 'team') => Promise<void>;
  cancelSubscription: (reason?: string) => Promise<boolean>;
  manageSubscription: () => Promise<void>;
  verifyCheckout: (checkoutId: string) => Promise<PolarCheckoutStatus>;
  clearError: () => void;
  
  // 상태 유틸리티
  hasActiveSubscription: boolean;
  isSubscriptionCanceled: boolean;
}

// ============================================
// usePayment Hook
// ============================================

export function usePayment(): UsePaymentReturn {
  const { user, session } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<PolarSubscription | null>(null);
  
  // ============================================
  // 구독 상태 조회
  // ============================================
  
  const loadSubscription = useCallback(async () => {
    if (!user || !session) {
      setSubscription(null);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const sub = await getPolarSubscription();
      setSubscription(sub);
    } catch (err) {
      // 프로덕션에서 민감 정보 노출 방지: 에러 코드만 로깅
      if (err instanceof ApiException) {
        console.error('[usePayment] Subscription load failed:', err.code);
        // 404는 구독 없음 - 에러로 처리하지 않음
        if (err.code !== 'NOT_FOUND') {
          setError(err.message);
        }
      } else {
        console.error('[usePayment] Subscription load failed: UNKNOWN_ERROR');
      }
      setSubscription(null);
    } finally {
      setIsLoading(false);
    }
  }, [user, session]);
  
  // 사용자 변경 시 구독 상태 로드
  useEffect(() => {
    loadSubscription();
  }, [user?.id, loadSubscription]);
  
  // ============================================
  // Polar Checkout (redirect 방식)
  // ============================================
  
  const checkout = useCallback(async (tier: 'starter' | 'pro' | 'team') => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { checkout_url } = await createPolarCheckout(tier, user?.email);
      
      // Polar checkout 페이지로 안전하게 redirect (Open Redirect 방지)
      safeRedirect(checkout_url, '/pricing');
    } catch (err) {
      const message = err instanceof ApiException 
        ? err.message 
        : '결제 페이지 생성에 실패했습니다';
      setError(message);
      setIsLoading(false);
      throw new Error(message);
    }
  }, [user?.email]);
  
  // ============================================
  // Checkout 상태 확인 (성공 페이지용)
  // ============================================
  
  const verifyCheckout = useCallback(async (checkoutId: string): Promise<PolarCheckoutStatus> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const status = await getPolarCheckoutStatus(checkoutId);
      
      // 결제 확인되면 구독 상태 새로고침
      if (status.status === 'confirmed') {
        await loadSubscription();
      }
      
      return status;
    } catch (err) {
      const message = err instanceof ApiException 
        ? err.message 
        : '결제 상태 확인에 실패했습니다';
      setError(message);
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  }, [loadSubscription]);
  
  // ============================================
  // 구독 취소
  // ============================================
  
  const cancelSubscription = useCallback(async (reason?: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    
    try {
      await cancelPolarSubscription(reason);
      
      // 구독 상태 새로고침
      await loadSubscription();
      
      return true;
    } catch (err) {
      const message = err instanceof ApiException 
        ? err.message 
        : '구독 취소에 실패했습니다';
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [loadSubscription]);
  
  // ============================================
  // 구독 관리 포털
  // ============================================
  
  const manageSubscription = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { url } = await createPolarPortalSession();
      // Polar 포털로 안전하게 redirect (Open Redirect 방지)
      safeRedirect(url, '/billing');
    } catch (err) {
      const message = err instanceof ApiException 
        ? err.message 
        : '구독 관리 페이지 접근에 실패했습니다';
      setError(message);
      setIsLoading(false);
      throw new Error(message);
    }
  }, []);
  
  // ============================================
  // 에러 초기화
  // ============================================
  
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  
  // ============================================
  // 유틸리티 값
  // ============================================
  
  const hasActiveSubscription = subscription?.status === 'active' || subscription?.status === 'trialing';
  const isSubscriptionCanceled = subscription?.cancel_at_period_end === true;
  
  return {
    isLoading,
    error,
    subscription,
    loadSubscription,
    checkout,
    cancelSubscription,
    manageSubscription,
    verifyCheckout,
    clearError,
    hasActiveSubscription,
    isSubscriptionCanceled,
  };
}
